/* eslint-disable fp/no-mutation */
/* eslint-disable fp/no-mutating-methods */
import React, { ReactElement, useEffect, useState } from 'react'
import { AsyncThunk } from '@reduxjs/toolkit/dist/createAsyncThunk'
import dayjs from 'dayjs'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import Select from '@mui/material/Select'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  AddIcon,
  CancelIcon,
  CorrectIcon,
  CredentialIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  IncorrectIcon,
  PreviewIcon,
  SearchIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
  VerifyIcon,
} from 'src/v3/core/icons'

import theme from 'src/theme'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers'

import { useAppDispatch } from 'src/store/hooks'
import { useFormik } from 'formik'
import * as yup from 'yup'
import axios, { pluckData } from 'src/utilities/http-client'
import { FlatCredential } from 'src/v3/core/types'
import {
  ContentType,
  contentTypes,
  downloadFile,
  FileData,
} from 'src/v3/utilities/filesystem'
import ConfirmAction from 'src/v3/components/confirm-action'

type Domain = 'manage' | 'profile'
type SortOption = 'name-az' | 'name-za' | 'date-new' | 'date-old'

let extensions: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/jpeg': 'jpeg',
  'image/png': 'png',
}

let verificationStatusStyles: Record<
  string,
  { status: string; color: string }
> = {
  accepted: {
    status: 'Accepted',
    color: theme.palette.success.main,
  },
  rejected: {
    status: 'Rejected',
    color: theme.palette.error.main,
  },
  pending: {
    status: 'Pending',
    color: theme.palette.warning.main,
  },
}

let getCredential = (id: string, domain: Domain) =>
  axios
    .get<string>(`/api/v2/${domain}/credential/image-url?CredentialID=${id}`)
    .then(pluckData)

let updateCredentialImage = (
  data: {
    id: string
    userID?: string
    imageType: string
  },
  domain: Domain,
) =>
  axios.put<string>(`/api/v2/${domain}/credential/image`, data).then(pluckData)

let deleteCredential = (id: string) =>
  axios.delete(`/api/v2/profile/delete-credential?CredentialID=${id}`)

function prepareCredentials(
  credentials: FlatCredential[],
  credentialSearch: string,
  credentialSort: SortOption,
): FlatCredential[] {
  return credentials
    .filter((c) =>
      c.credentialType.toLowerCase().includes(credentialSearch.toLowerCase()),
    )
    .sort((a, b) => {
      if (credentialSort === 'name-az') {
        return a.credentialType.localeCompare(b.credentialType || '') || 0
      }
      if (credentialSort === 'name-za') {
        return b.credentialType.localeCompare(a.credentialType || '') || 0
      }
      if (credentialSort === 'date-old') {
        return dayjs(a.createdTime).diff(b.createdTime)
      }
      return dayjs(b.createdTime).diff(a.createdTime)
    })
}

export let CredentialList = ({
  credentials,
  verificationEnabled,
  fetchCredentials,
  setErrorMessage,
  userID,
}: {
  credentials: FlatCredential[]
  verificationEnabled: boolean
  fetchCredentials: AsyncThunk<void, void, object>
  setErrorMessage: (value: React.SetStateAction<string>) => void
  userID?: string
}): ReactElement => {
  let dispatch = useAppDispatch()

  let [credentialSearch, setCredentialSearch] = useState<string>('')
  let [credentialSort, setCredentialSort] = useState<SortOption>('date-new')
  let [credentialTab, setCredentialTab] = useState<string>('active')
  let [viewBlob, setViewBlob] = useState<Blob | null>(null)
  let [downloading, setDownloading] = useState<string>('')
  let [deleting, setDeleting] = useState<string>('')
  let [saving, setSaving] = useState<boolean>(false)
  let [formOpen, setFormOpen] = useState<boolean>(false)
  let [fileToUpload, setFileToUpload] = useState<FileData | undefined>(
    undefined,
  )
  let [existingFile, setExistingFile] = useState<string | undefined>('')
  let [infoOpen, setInfoOpen] = useState<boolean>(false)
  let [verifyOpen, setVerifyOpen] = useState<string>('')
  let [verificationStatus, setVerificationStatus] = useState<string>('accepted')
  let [rejectionReason, setRejectionReason] = useState<string>('')

  let domain: Domain = userID ? 'manage' : 'profile'

  let filteredCredentials = [...credentials]

  if (credentialTab === 'active') {
    filteredCredentials = credentials.filter(
      (c) => !dayjs(c.expiration).isBefore(dayjs()),
    )
  } else if (credentialTab === 'expired') {
    filteredCredentials = credentials.filter((c) =>
      dayjs(c.expiration).isBefore(dayjs()),
    )
  } else if (credentialTab === 'pending') {
    filteredCredentials = credentials.filter(
      (c) =>
        !dayjs(c.expiration).isBefore(dayjs()) &&
        c.verificationStatus === 'pending',
    )
  }

  filteredCredentials = prepareCredentials(
    filteredCredentials,
    credentialSearch,
    credentialSort,
  )

  let form = useFormik({
    validationSchema: yup.object({
      type: yup.string().required(),
      expiration: yup.string().required(),
      credentialIDNumber: yup.string().required(),
      other: yup.string().when('type', (credType, schema) => {
        if (credType === 'Other') {
          return schema.required()
        }
        return schema
      }),
    }),
    enableReinitialize: true,
    initialValues: {
      id: '',
      type: '',
      expiration: '',
      credentialIDNumber: '',
      other: '',
    },
    onSubmit: (values) => {
      setSaving(true)
      if (!values.id) {
        axios
          .post(`/api/v2/${domain}/credential`, {
            userID,
            type:
              values.type === 'Other' ? { other: values.other } : values.type,
            expiration: values.expiration,
            credentialIDNumber: values.credentialIDNumber,
          })
          .then(({ data }) =>
            updateCredentialImage(
              {
                id: data.id,
                userID,
                imageType:
                  fileToUpload?.extension === 'jpeg'
                    ? 'jpg'
                    : fileToUpload?.extension === 'png'
                      ? 'png'
                      : 'pdf',
              },
              domain,
            ),
          )
          .then((response) =>
            axios.put(response, fileToUpload?.file, {
              headers: {
                'Cache-Control': 'max-age=2592000',
                'content-type': fileToUpload?.type,
              },
            }),
          )
          .then(() => dispatch(fetchCredentials()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .then(() => setInfoOpen(verificationEnabled && domain === 'profile'))
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Creating Credential')
            dispatch(fetchCredentials())
            setFormOpen(false)
            setSaving(false)
          })
      } else {
        axios
          .put(`/api/v2/${domain}/credential`, {
            id: values.id,
            userID,
            type:
              values.type === 'Other' ? { other: values.other } : values.type,
            expiration: values.expiration,
            credentialIDNumber: values.credentialIDNumber,
          })
          .then(() => {
            if (fileToUpload) {
              updateCredentialImage(
                {
                  id: values.id,
                  userID,
                  imageType: fileToUpload.extension === 'jpeg' ? 'jpg' : 'png',
                },
                domain,
              ).then((response) =>
                axios.put(response, fileToUpload?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': fileToUpload?.type,
                  },
                }),
              )
            }
          })
          .then(() => dispatch(fetchCredentials()))
          .then(() => setFormOpen(false))
          .then(() => setSaving(false))
          .then(() => form.resetForm())
          .catch((err) => {
            setErrorMessage(err?.message || 'Error Updating Credential')
            dispatch(fetchCredentials())
            setFormOpen(false)
            setSaving(false)
          })
      }
    },
  })

  let handleCapture = (event: React.ChangeEvent) => {
    let file = (event.target as HTMLInputElement).files?.[0]
    let fileName = file?.name || ''
    let reader = new FileReader()

    reader.onloadend = () => {
      let fileData = reader.result
        ?.toString()
        ?.match(/data:[a-z]+\/[a-z]+/)?.[0]
      if (!fileData) {
        setErrorMessage('Credential Upload Failed: Read File Failed')
        return
      }

      let fileType = fileData.split(':')[1]
      let fileExtension = fileType.split('/')[1]

      if (!contentTypes.includes(fileType as ContentType)) {
        setErrorMessage(`Invalid File Type ${fileType}`)
        return
      }

      let binary = atob(reader.result?.toString().split(',')[1] as string)
      let array = new Array(binary.length)
        .fill('')
        .map((_, i) => binary.charCodeAt(i))
      let blobData = new Blob([new Uint8Array(array)], { type: fileExtension })

      setFileToUpload({
        name: fileName,
        type: fileType,
        extension: fileExtension,
        file: blobData,
      })
    }

    reader.readAsDataURL(file!)
  }

  let onView = (id: string) => {
    setDownloading(id)
    getCredential(id, domain)
      .then((data) =>
        axios.get<Blob>(data, { responseType: 'blob' }).then(pluckData),
      )
      .then((data) => setViewBlob(data))
      .catch((err) =>
        setErrorMessage(
          err?.response?.data || err?.message || 'Error Getting Credential',
        ),
      )
      .then(() => setDownloading(''))
  }

  let onEdit = (id: string) => {
    let credEdit = credentials.find((c) => c.id === id)
    form.setFieldValue('id', credEdit?.id)
    form.setFieldValue('expiration', credEdit?.expiration)
    form.setFieldValue('credentialIDNumber', credEdit?.credentialIDNumber)
    if (typeof credEdit?.type === 'string') {
      form.setFieldValue('type', credEdit?.type)
    } else {
      form.setFieldValue('type', 'Other')
      form.setFieldValue('other', credEdit?.credentialType)
    }
    setExistingFile(credEdit?.imageName)
    setFormOpen(true)
  }

  let onCredentialVerification = () => {
    setSaving(true)
    axios
      .put('/api/v2/manage/credential/verification-status', {
        id: verifyOpen,
        userID,
        verificationStatus,
        reason: verificationStatus === 'rejected' ? rejectionReason : undefined,
      })
      .catch((err) => {
        setErrorMessage(
          err?.response?.data || err?.message || 'Error Verifying Credential',
        )
      })
      .then(() => dispatch(fetchCredentials()))
      .then(() => setVerificationStatus('accepted'))
      .then(() => setRejectionReason(''))
      .then(() => setSaving(false))
      .then(() => setVerifyOpen(''))
  }

  useEffect(() => {
    if (!formOpen) {
      setExistingFile('')
      setFileToUpload(undefined)
      form.resetForm()
    }
  }, [formOpen])

  useEffect(() => {
    if (form.values.type !== 'Other') {
      form.setFieldValue('other', '')
    }
  }, [form.values.type])

  return (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap-reverse', mt: '-6px' }}>
        <Tabs
          variant="scrollable"
          value={credentialTab}
          onChange={(e, num) => setCredentialTab(num)}
        >
          <Tab value="active" label="Active" />
          {verificationEnabled && <Tab value="pending" label="Pending" />}
          <Tab value="expired" label="Expired" />
          <Tab value="all" label="All" />
          <Divider orientation="vertical" flexItem />
        </Tabs>
        <Box style={{ display: 'flex', gap: '2px', padding: '0 8px' }}>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={() => setFormOpen(true)}
          >
            Create
          </Button>
        </Box>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          my: 2,
        }}
      >
        <TextField
          sx={{ width: 240 }}
          variant="outlined"
          size="small"
          label="Search Credentials"
          placeholder="Credential Type"
          name="credential-search"
          type="text"
          value={credentialSearch}
          onChange={(event: React.ChangeEvent) => {
            setCredentialSearch((event.target as HTMLInputElement).value)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControl sx={{ width: 160 }}>
          <InputLabel size="small">Sort</InputLabel>
          <Select
            value={credentialSort}
            label="Sort"
            variant="outlined"
            size="small"
            onChange={(event) =>
              setCredentialSort(event.target.value as SortOption)
            }
          >
            <MenuItem value={'date-new'}>Date: Newest</MenuItem>
            <MenuItem value={'date-old'}>Date: Oldest</MenuItem>
            <MenuItem value={'name-az'}>Name: A-Z</MenuItem>
            <MenuItem value={'name-za'}>Name: Z-A</MenuItem>
          </Select>
        </FormControl>
      </Box>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 15,
          marginBottom: 15,
          overflowY: 'scroll',
          maxHeight: 500,
        }}
      >
        {filteredCredentials.length ? (
          filteredCredentials.map((credential) => (
            <Card
              key={credential.id}
              variant="outlined"
              sx={{
                p: 2,
                width: 340,
                textAlign: 'center',
                flex: '0 0 auto',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="h6">
                  <CredentialIcon className="mb-1 mr-1" color="primary" />
                  {typeof credential.type === 'string'
                    ? credential.credentialType.toUpperCase()
                    : credential.credentialType}
                </Typography>
                <Typography color="text.secondary" variant="subtitle2">
                  {credential.credentialIDNumber}
                </Typography>
              </Box>
              <Box sx={{ mb: 1 }}>
                {dayjs(credential.expiration).isBefore(dayjs()) ? (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      <IncorrectIcon color="error" />
                      <Typography color="error">Expired</Typography>
                    </Box>
                    <Typography>
                      Expired{' '}
                      {dayjs(credential.expiration).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                ) : (
                  <Box>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '2px',
                      }}
                    >
                      <CorrectIcon color="success" />
                      <Typography color={theme.palette.success.main}>
                        Active
                      </Typography>
                    </Box>
                    <Typography>
                      Expires{' '}
                      {dayjs(credential.expiration).format('MM/DD/YYYY')}
                    </Typography>
                  </Box>
                )}
                {verificationEnabled && (
                  <Box sx={{ mt: 1 }}>
                    <Typography>
                      <span>Verification </span>
                      <span
                        style={{
                          color:
                            verificationStatusStyles[
                              credential.verificationStatus
                            ].color,
                          fontWeight: 'bold',
                        }}
                      >
                        {
                          verificationStatusStyles[
                            credential.verificationStatus
                          ].status
                        }
                      </span>
                    </Typography>
                    {domain === 'manage' &&
                      credential.verificationStatus === 'pending' && (
                        <Box
                          sx={{
                            mt: '2px',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VerifyIcon />}
                            onClick={() => setVerifyOpen(credential.id)}
                          >
                            Verify Credential
                          </Button>
                        </Box>
                      )}
                  </Box>
                )}
              </Box>
              {(downloading || deleting) === credential.id ? (
                <CircularProgress size="28px" sx={{ margin: 'auto' }} />
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <Tooltip
                    title={
                      credential.imageName.includes('pdf')
                        ? 'Cannot preview PDF files. Download the file to view.'
                        : 'View'
                    }
                  >
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => onView(credential.id)}
                        disabled={
                          !credential.imageName ||
                          credential.imageName.includes('pdf')
                        }
                      >
                        <PreviewIcon />
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!credential.imageName}
                        onClick={() => {
                          setDownloading(credential.id)
                          getCredential(credential.id, domain)
                            .then((response) =>
                              axios
                                .get<Blob>(response, { responseType: 'blob' })
                                .catch(() => {
                                  // eslint-disable-next-line fp/no-throw, no-throw-literal
                                  throw 'File Not Found'
                                }),
                            )
                            .then(pluckData)
                            .then((response) =>
                              downloadFile(
                                response,
                                `${
                                  credential.credentialType || credential.id
                                }.${extensions[response.type]}`,
                              ),
                            )
                            .then(() => setDownloading(''))
                            .catch((err) => {
                              setDownloading('')
                              setErrorMessage(
                                `Image Download Failed: ${err.response?.data || err}`,
                              )
                            })
                        }}
                      >
                        <DownloadIcon />
                      </Button>
                    </span>
                  </Tooltip>
                  <Tooltip title="Edit">
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => onEdit(credential.id)}
                    >
                      <EditIcon />
                    </Button>
                  </Tooltip>
                  {domain === 'profile' && (
                    <>
                      <Tooltip title="Delete">
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setDeleting(`delete-${credential.id}`)}
                        >
                          <DeleteIcon />
                        </Button>
                      </Tooltip>
                      <ConfirmAction
                        executeAction={() =>
                          deleteCredential(credential.id).then(() =>
                            dispatch(fetchCredentials()),
                          )
                        }
                        open={deleting}
                        setOpen={setDeleting}
                        openText={`delete-${credential.id}`}
                        title="Confirm Deletion"
                        text="Are you sure you want to delete this credential?"
                        onFailure={(err) =>
                          setErrorMessage(
                            err.message || 'Error Deleting Credential',
                          )
                        }
                      />
                    </>
                  )}
                </Box>
              )}
            </Card>
          ))
        ) : (
          <Typography color="text.secondary">
            There are no credentials matching these filters.
          </Typography>
        )}
      </div>
      <Dialog open={formOpen} onClose={() => setFormOpen(false)}>
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <form onSubmit={form.handleSubmit}>
            <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
              Credential
            </DialogTitle>
            <DialogContent
              sx={{
                px: 2,
                pb: 2,
                display: 'flex',
                gap: 2,
                flexDirection: 'column',
              }}
            >
              <TextField
                sx={{ mt: 1 }}
                variant="outlined"
                size="small"
                label="Credential ID Number"
                name="credentialIDNumber"
                type="text"
                fullWidth
                value={form.values.credentialIDNumber}
                onChange={form.handleChange}
                error={
                  form.touched.credentialIDNumber &&
                  !!form.errors.credentialIDNumber
                }
              />
              <div className="flex justify-center">
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    value={dayjs(form.values.expiration)}
                    onChange={(val: any) =>
                      form.setFieldValue(
                        'expiration',
                        dayjs(val).format('MM/DD/YYYY'),
                      )
                    }
                  />
                </LocalizationProvider>
              </div>
              <FormControl fullWidth>
                <InputLabel size="small">Credential Type</InputLabel>
                <Select
                  name="type"
                  value={form.values.type}
                  label="Credential Type"
                  onChange={form.handleChange}
                  variant="outlined"
                  size="small"
                  error={form.touched.type && !!form.errors.type}
                >
                  <MenuItem value="unselected">Unselected</MenuItem>
                  {[
                    'BLS',
                    'ACLS',
                    'PALS',
                    'NRP',
                    'PHTLS',
                    'CCP-C',
                    'FP-C',
                    'CFRN',
                    'Other',
                  ].map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {form.values.type === 'Other' && (
                <TextField
                  variant="outlined"
                  size="small"
                  label="Custom Credential Type"
                  name="other"
                  type="text"
                  fullWidth
                  value={form.values.other}
                  onChange={form.handleChange}
                  error={form.touched.other && !!form.errors.other}
                />
              )}
              <Box>
                <label style={{ marginTop: 0 }}>
                  <Button
                    component="span"
                    variant="outlined"
                    startIcon={<UploadIcon />}
                  >
                    <input
                      accept="image/*,.pdf"
                      style={{ display: 'none' }}
                      type="file"
                      onChange={(e) => handleCapture(e)}
                    />
                    {!form.values.id || !existingFile ? 'Select' : 'Overwrite'}{' '}
                    File
                  </Button>
                </label>
                {fileToUpload && (
                  <Typography variant="subtitle1">
                    <UploadFileIcon
                      color="primary"
                      sx={{ mr: '2px', mb: '2px' }}
                    />
                    {fileToUpload.name}
                  </Typography>
                )}
                {existingFile && (
                  <Typography color="text.secondary" variant="subtitle2">
                    *The existing credential file will only be overwritten upon
                    submitting this form with a new image uploaded.
                  </Typography>
                )}
              </Box>
            </DialogContent>
            <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
              {saving ? (
                <CircularProgress className="m-auto" size={36} />
              ) : (
                <>
                  <Button
                    type="submit"
                    color="primary"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<SubmitIcon />}
                    disabled={!existingFile && !fileToUpload}
                  >
                    Submit
                  </Button>
                  <Button
                    onClick={() => setFormOpen(false)}
                    color="error"
                    variant="contained"
                    sx={{ flex: '1 1 0px' }}
                    startIcon={<CancelIcon />}
                  >
                    Cancel
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </Paper>
      </Dialog>
      <Dialog open={!!verifyOpen} onClose={() => setVerifyOpen('')}>
        <Paper sx={{ width: 300, px: 2, py: 1 }}>
          <DialogTitle sx={{ textAlign: 'center', mb: -1.5 }}>
            Credential Verification
          </DialogTitle>
          <DialogContent
            sx={{
              px: 2,
              pb: 2,
              display: 'flex',
              gap: 1,
              flexDirection: 'column',
            }}
          >
            <FormControl>
              <RadioGroup
                row
                value={verificationStatus}
                onChange={(e) => setVerificationStatus(e.target.value)}
              >
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="accepted"
                  control={<Radio />}
                  label="Accept"
                />
                <FormControlLabel
                  sx={{ m: 0 }}
                  value="rejected"
                  control={<Radio />}
                  label="Reject"
                />
              </RadioGroup>
            </FormControl>
            {verificationStatus === 'rejected' && (
              <TextField
                variant="outlined"
                size="small"
                label="Rejection Reason"
                fullWidth
                multiline
                maxRows={10}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
              />
            )}
          </DialogContent>
          <DialogActions className="flex justify-center pt-0 pb-3 mt-0">
            {saving ? (
              <CircularProgress className="m-auto" size={36} />
            ) : (
              <>
                <Button
                  onClick={onCredentialVerification}
                  color="primary"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<SubmitIcon />}
                  disabled={
                    verificationStatus === 'rejected' && !rejectionReason
                  }
                >
                  Submit
                </Button>
                <Button
                  onClick={() => setVerifyOpen('')}
                  color="error"
                  variant="contained"
                  sx={{ flex: '1 1 0px' }}
                  startIcon={<CancelIcon />}
                >
                  Cancel
                </Button>
              </>
            )}
          </DialogActions>
        </Paper>
      </Dialog>
      <Dialog
        open={!!viewBlob && !formOpen}
        onClose={() => setViewBlob(null)}
        maxWidth="lg"
      >
        <DialogContent>
          {viewBlob && <img src={URL.createObjectURL(viewBlob)} />}
        </DialogContent>
      </Dialog>
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}>
        <DialogContent>
          Your manager will verify your credential. You will receive an email
          once it is accepted or rejected.
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setInfoOpen(false)}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
